import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges, DestroyRef } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { ScreensStateService } from '../../core/services/screen-state/screens-state.service';
import { MonitorMenuService } from '../../core/services/dialog-services/monitor-menu.service';
import { PlaylistStateService } from '../../core/services/screen-state/playlists-state.service';
import { MonitorsService } from '../../core/services/http-services/monitors.service';
import { ScheduleSettingsDialogService } from '../../core/services/dialog-services/schedule-dialog.service';
import { UIService } from '../../core/services/utils/ui.service';
import { PlaylistsService } from '../../core/services/http-services/playlists.service';

import { BaseViewItemComponent } from '../_basic/item-view-base.component';
import { Schedule } from '../../core/models/entities/schedule.type';
import { ScheduleSettingsMode } from '../../core/enums/screen-settings-mode.enum';
import { ScheduleModalMode } from '../../core/enums/schedule-model-mode.enum';
import { StartOptions } from '../../core/models/enums/start-options.enum';
import { PlaylistDTO } from '../../core/models/view-models/playlist-models/playlist-dto.model';
import { TagService } from '../../core/services/http-services/tag.service';
import { TagViewModel } from '../../core/models/view-models/tag-models/tag-view.model';
import { MonitorKey } from '../../core/models/view-models/monitor-models/monitor-key.type';
import { DropItem } from '../../core/interfaces/drop-item.model';

@Component({
  selector: 'list-item',
  styleUrls: ['./list-item.component.scss'],
  templateUrl: './list-item.component.html'
})
export class ListItemComponent extends BaseViewItemComponent implements OnDestroy, OnChanges, OnInit {
  @Input() idPlaylist: number;

  selectedPlaylists: PlaylistDTO[] = [];
  playlists: PlaylistDTO[] = [];
  startOptions: StartOptions[] = [StartOptions.Default, StartOptions.Playlist];
  isDropActive: boolean;
  isDropEnabled: boolean;
  assignedMonitorTags$: Observable<TagViewModel[]>;

  constructor(
    public selectionService: ScreensStateService,
    public monitorMenuService: MonitorMenuService,
    public monitorDataService: MonitorsService,
    public scheduleModalService: ScheduleSettingsDialogService,
    public playlistStateService: PlaylistStateService,
    public readonly tagService: TagService,
    public readonly uiService: UIService,
    private readonly destroyRef: DestroyRef,
    private readonly playlistDataService: PlaylistsService
  ) {
    super(selectionService, monitorMenuService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    const playlistCollectionSub = this.playlistDataService.playlistCollectionChanged.subscribe(
      data => {
        this.playlists = PlaylistStateService.filterPlaylistsByScreens(data, [
          this.monitor
        ]);

        this.selectedPlaylists = this.findAssignedPlaylist(
          this.idPlaylist,
          this.playlists
        );
      }
    );

    this.subscriptions.add(playlistCollectionSub);

    this.assignedMonitorTags$ = this.tagService.tagCollectionChanged.pipe(
      mergeMap(() => {
        const currentMonitorKey = new MonitorKey(this.monitor.MonitorHandler, this.monitor.MachinePhysicalAddress);
        return this.tagService.getMonitorTagByMonitorKey(currentMonitorKey);
      })
    );

    this.uiService.itemlistDragSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(dragData => {
        this.isDropEnabled = dragData &&
          this.monitor &&
          (!dragData.data?.MachinePhysicalAddress || dragData.data.MachinePhysicalAddress === this.monitor.MachinePhysicalAddress);
      });

    this.uiService.dragActivationSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state =>
        this.isDropActive = state && this.isDropEnabled && this.isSelected
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.IdPlaylist && this.playlists) {
      this.selectedPlaylists = this.findAssignedPlaylist(
        changes.IdPlaylist.currentValue,
        this.playlists
      );
    }
  }

  findAssignedPlaylist(id, collection) {
    const playlist = collection.find(x => x.Id === id);
    return playlist ? [playlist] : [];
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public onContextMenu(event) {
    event.preventDefault();
    event.stopPropagation();

    this.selectionService.selectScreen(this.monitor, true);
    this.uiService.hideBottomPanel();

    this.monitorMenuService.showMonitorMenu(
      this.monitor,
      event.clientX,
      event.clientY,
      true
    );
  }

  startPlaylist() {
    this.selectionService.selectScreen(this.monitor, true);
    this.monitor.playAssigned();
  }

  stopPlaylist() {
    this.selectionService.selectScreen(this.monitor, true);
    this.monitor.stopPlaylist();
  }

  onPlaylistSelectChanged(selectedPlaylist: PlaylistDTO) {
    const id = selectedPlaylist.Id;
    if (id) {
      if (id !== this.monitor.IdAssignedPlaylist) {
        const playlist = this.playlists.find(x => x.Id === id);
        if (playlist) {
          this.monitor.assignPlaylist(playlist);
        }
      }
    }
  }

  refreshStartOptions(startOption: StartOptions) {
    this.monitor.changeStartOptions(startOption);
  }

  schedule() {
    if (this.selectedPlaylists.length) {
      const schedule = new Schedule();
      schedule.PlaylistId = this.selectedPlaylists[0].Id;
      schedule.ScheduleType = ScheduleSettingsMode.Daily;
      schedule.Monitors = [
        {
          MonitorHandler: this.monitor.MonitorHandler,
          MachinePhysicalAddress: this.monitor.MachinePhysicalAddress
        }
      ];

      this.scheduleModalService.show(schedule, ScheduleModalMode.Create);
    }
  }

  mouseLeave(): void {
    if (this.isDropEnabled) {
      this.uiService.handleDragActivation(false);
    }
    this.isDropActive = this.isSelected && this.isDropActive;
  }

  mouseEnter(): void {
    if (this.isDropEnabled) {
      this.uiService.handleDragActivation(true);
    }
    this.isDropActive = this.isDropEnabled;
  }

  enterPredicate(drag: DropItem, drop: DropItem): boolean {
    return drag.data &&
      (!drag.data.MachinePhysicalAddress ||
        drag.data.MachinePhysicalAddress === drop.data.MachinePhysicalAddress);
  }

  drop(event: CdkDragDrop<string[]>): void {
    this.selectionService.dropItem(event, this.monitor, this.isSelected);
  }
}
